/**
 * Turning a failed request into something a reader can act on.
 *
 * The server names the failures a user is meant to see, and this looks the name
 * up in the catalog. Anything unnamed keeps the server's English prose: an
 * untranslated message about an invalid editor ID is still more use than a
 * generic apology, and those paths are diagnostics rather than copy.
 */
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import type { ErrorCode as StudioErrorCode } from "@scandi/shared";
import { ApiError } from "@/lib/api";
import type { Translate } from "@/i18n/translate";

export function useErrorMessage() {
  const { t, i18n } = useTranslation();

  return useCallback(
    (error: unknown): string | null => {
      if (error == null) return null;

      if (!(error instanceof ApiError)) {
        // Not a reply at all — the request never arrived. Saying "something went
        // wrong" here would hide the one useful thing we know.
        return t("login.unreachable");
      }

      switch (error.code) {
        case "password_required":
          return t("errors.passwordRequired");
        case "incorrect_password":
          return t("errors.incorrectPassword");
        case "rate_limited":
          return t("errors.rateLimited", { wait: wait(error.retryAfter, t) });
        case "not_signed_in":
          return t("errors.notSignedIn");
        case "admin_required":
          return t("errors.adminRequired");
        case "strategist_required":
          return t("errors.strategistRequired");
        case "pipeline_required":
          return t("errors.pipelineRequired");
        case "no_access":
          return t("errors.noAccess");
        case "account_disabled":
          return t("errors.accountDisabled");
        case "google_not_configured":
          return t("errors.googleNotConfigured");
        case "invalid_google_token":
          return t("errors.invalidGoogleToken");
        case "email_not_verified":
          return t("errors.emailNotVerified");
        case "editor_link_required":
          return t("errors.editorLinkRequired");
        case "strategist_link_required":
          return t("errors.strategistLinkRequired");
        case "editor_already_linked":
          return t("errors.editorAlreadyLinked");
        case "strategist_already_linked":
          return t("errors.strategistAlreadyLinked");
        case "cannot_change_own_access":
          return t("errors.cannotChangeOwnAccess");
        case "bootstrap_admin_undeletable":
          return t("errors.bootstrapAdminUndeletable");
        case "script_chat_role":
          return t("errors.scriptChatRole");
        case "agent_unavailable":
          return t("errors.agentUnavailable");
        case "pipeline_conflict":
          return t("errors.pipelineConflict");
        case "images_only":
          return t("errors.imagesOnly");
        case "brief_not_found":
          return t("errors.briefNotFound");
        case "pipeline_forbidden_transition":
          return t("errors.pipelineForbiddenTransition");
        case "pipeline_not_assigned":
          return t("errors.pipelineNotAssigned");
        case "pipeline_transition_blocked":
          // The server names the missing piece, and it is the whole point of
          // the message — "falta el enlace de entrega" is actionable in a way
          // that a generic "no se pudo" is not.
          return error.fromServer ? error.message : t("errors.pipelineTransitionBlocked");
        case "upload_too_large":
        case "payload_too_large":
          return t("errors.uploadTooLarge");
        case "unsupported_file_type":
          return t("errors.unsupportedFileType");
        // Storyboards speak the agent API's vocabulary (see ApiErrorCode).
        // On the portal only the storyboard routes answer with these codes:
        // the rest name their own (`brief_not_found`, `pipeline_conflict`),
        // and the deck service's codes stop at its one portal route (deck
        // visibility answers its own 404; anything else is a 500 with no
        // code). A route that starts sending them gets these sentences.
        case "conflict":
          // A stale revision is the everyday case: two people on one
          // storyboard. A conflict with no revision is a key already taken,
          // and the server's sentence names it better than a guess would.
          if (error.revision !== null) return t("errors.storyboardConflict", { revision: error.revision });
          return error.fromServer ? error.message : t("errors.storyboardConflictPlain");
        case "not_found":
          return t("errors.notFound");
        case "invalid_body":
        case "invalid_parameter":
          // The message names the field ("text: too long"), which is a
          // diagnostic rather than copy; the translated line says what to do.
          return t("errors.invalidBody");
        case "forbidden":
          return t("errors.forbidden");
        case "not_ready":
          return t("errors.notReady");
        case "storage_unavailable":
          return t("errors.storageUnavailable");
        case "invalid_phone":
          return t("notify.contact.invalidPhone");
        case "invalid_wa_username":
          return t("notify.contact.invalidUsername");
        case "invalid_wa_contact":
          return t("notify.contact.both");
        default:
          // The studio's and the agents' admin API's codes (CONTRACTS.md §19)
          // each have a sentence in `studio.errors`. The cases above keep the
          // platform's own wording for the codes the two APIs share.
          if (error.code && i18n.exists(`studio.errors.${error.code}`)) {
            return t(`studio.errors.${error.code as StudioErrorCode}`);
          }
          return error.fromServer
            ? error.message
            : t("errors.requestFailed", { status: error.status });
      }
    },
    [t, i18n],
  );
}

/**
 * "45 seconds" or "15 minutes".
 *
 * Built from the server's seconds rather than parsed out of its message, so the
 * unit can be whichever one reads naturally in the language — and so the number
 * is still right if the lockout is ever reconfigured.
 */
function wait(seconds: number | null, t: Translate): string {
  if (seconds === null) return t("errors.waitMinutes", { count: 15 });
  return seconds < 60
    ? t("errors.waitSeconds", { count: seconds })
    : t("errors.waitMinutes", { count: Math.ceil(seconds / 60) });
}
